import React, { useContext, useRef } from 'react';
import realtyOneLogo from 'images/realty-one-group-color-large.png';
import { MLSMapContext } from '../../context/MLSMapContext';
import useWindowSize from '../../hooks/useWindowSize';
import FilterButton from '../FilterButton';
import PaginationResults from '../Map/PaginationResults';
import shortenMoneyValue from '../../utils/shortenMoneyValue';
import mls_categories from '../../constants/mls_categories.json';
import mls_listing_statuses from '../../constants/mls_listing_statuses.json';
import DropdownContainer from '../DropdownContainer';
import MLSTypeDropdown from './MLSTypeDropdown';
import MLSCategoryDropdown from './MLSCategoryDropdown';
import MLSSortDropdown from './MLSSortDropdown';
import MLSStatusDropdown from './MLSStatusDropdown';

export default function MLSFilterBar() {
  const typesRef = useRef();
  const categoriesRef = useRef();
  const bedsRef = useRef();
  const priceRef = useRef();
  const filtersRef = useRef();
  const sortRef = useRef();

  const { width } = useWindowSize();
  const isMobile = width <= 768;

  const {
    meta,
    showFilters,
    setShowFilters,
    bedrooms,
    bathrooms,
    minPrice,
    maxPrice,
    clearFilters,
    sortDropdownVisible,
    setSortDropdownVisible,
    sortBy,
    setTypeDropdownVisible,
    typeFilter,
    categoryFilter,
    setCategoryDropdownVisible,
    typeDropdownVisible,
    categoryDropdownVisible,
    minSqFt,
    maxSqFt,
    minYearBuilt,
    maxYearBuilt,
    minLotSize,
    maxLotSize,
    filterStatuses,
    amenities,
    mapAreas,
    statusFilters,
    statusDropdownVisible,
    setStatusDropdownVisible,
  } = useContext(MLSMapContext);

  const bedBathButtonText = () => {
    if (bedrooms > 0 && !bathrooms) return `${bedrooms}+ Bedrooms`;
    if (bathrooms > 0 && !bedrooms) return `${bathrooms}+ Bathrooms`;
    if (bedrooms > 0 && bathrooms > 0)
      return `${bedrooms}+ BR • ${bathrooms}+ BA`;
    return 'BR • BA';
  };

  const priceButtonText = () => {
    if (minPrice && !maxPrice) return `Above $${shortenMoneyValue(minPrice)}`;
    if (maxPrice && !minPrice) return `Up to $${shortenMoneyValue(maxPrice)}`;
    if (minPrice && maxPrice)
      return `$${shortenMoneyValue(minPrice)} - $${shortenMoneyValue(
        maxPrice
      )}`;
    return 'Price';
  };

  const statusButtonText = () => {
    if (statusFilters.length === 1)
      return `${
        mls_listing_statuses.find(s => s.value === statusFilters[0].value).label
      }`;
    if (statusFilters.length > 1) return `${statusFilters.length} Statuses`;
    return 'Status';
  };

  const categoryButtonText = () => {
    if (categoryFilter.length === 1)
      return `${
        mls_categories.find(c => c.value === categoryFilter[0].value)
          .short_label
      }`;
    if (categoryFilter.length > 1) return `${categoryFilter.length} Categories`;
    return 'Category';
  };

  const activeFiltersCount = () => {
    let filtersActive = 0;
    if (minSqFt || maxSqFt) filtersActive += 1;
    if (minYearBuilt || maxYearBuilt) filtersActive += 1;
    if (minLotSize || maxLotSize) filtersActive += 1;
    if (filterStatuses.length > 0)
      filterStatuses.forEach(() => {
        filtersActive += 1;
      });
    if (amenities.length > 0)
      amenities.forEach(() => {
        filtersActive += 1;
      });
    if (mapAreas.length > 0)
      mapAreas.forEach(() => {
        filtersActive += 1;
      });
    return filtersActive;
  };

  const filterButtonText = () => {
    return activeFiltersCount() > 0
      ? `${activeFiltersCount()} Filters`
      : 'Filters';
  };

  return (
    <div className="filter-bar">
      <section className="filters-container">
        <div className="filter-button-group" ref={typesRef}>
          <FilterButton
            onClick={() => {
              setShowFilters(false);
              setCategoryDropdownVisible(false);
              setTypeDropdownVisible(true);
            }}
            className={`${typeFilter ? 'btn--dark' : 'btn--light'}`}
          >
            {typeFilter ? typeFilter.label : 'Type'}
          </FilterButton>
          {!isMobile && (
            <DropdownContainer visible={typeDropdownVisible}>
              <MLSTypeDropdown />
            </DropdownContainer>
          )}
        </div>
        {typeFilter && (typeFilter.value !== 'E' || typeFilter.value !== 'F') && (
          <div className="filter-button-group" ref={categoriesRef}>
            <FilterButton
              onClick={() => {
                setShowFilters(false);
                setTypeDropdownVisible(false);
                setCategoryDropdownVisible(true);
              }}
              className={`${
                categoryFilter.length ? 'btn--dark' : 'btn--light'
              }`}
            >
              {categoryButtonText()}
            </FilterButton>
            {!isMobile && (
              <DropdownContainer visible={categoryDropdownVisible}>
                <MLSCategoryDropdown />
              </DropdownContainer>
            )}
          </div>
        )}
        <div className="filter-button-group" ref={bedsRef}>
          <FilterButton
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              setShowFilters(!showFilters);
            }}
            className={`${
              bedrooms > 0 || bathrooms > 0 ? 'btn--dark' : 'btn--light'
            }`}
          >
            {bedBathButtonText()}
          </FilterButton>
        </div>
        <div className="filter-button-group" ref={priceRef}>
          <FilterButton
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              setShowFilters(!showFilters);
            }}
            className={`${minPrice || maxPrice ? 'btn--dark' : 'btn--light'}`}
          >
            {priceButtonText()}
          </FilterButton>
        </div>
        <div className="filter-button-group" ref={categoriesRef}>
          <FilterButton
            onClick={() => {
              setShowFilters(false);
              setStatusDropdownVisible(true);
            }}
            className={`${statusFilters.length ? 'btn--dark' : 'btn--light'}`}
          >
            {statusButtonText()}
          </FilterButton>
          {!isMobile && (
            <DropdownContainer visible={statusDropdownVisible}>
              <MLSStatusDropdown />
            </DropdownContainer>
          )}
        </div>
        <div className="filter-button-group" ref={filtersRef}>
          <FilterButton
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              setShowFilters(!showFilters);
            }}
            className={`${
              activeFiltersCount() > 0 ? 'btn--dark' : 'btn--light'
            }`}
          >
            {filterButtonText()}
          </FilterButton>
        </div>
        <div className="filter-button-group" ref={sortRef}>
          <FilterButton
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              setShowFilters(false);
              setSortDropdownVisible(!sortDropdownVisible);
            }}
            className={`${sortBy ? 'btn--dark' : 'btn--light'}`}
          >
            {sortBy ? sortBy.label : 'Sort'}
          </FilterButton>
          {!isMobile && (
            <DropdownContainer visible={sortDropdownVisible}>
              <MLSSortDropdown />
            </DropdownContainer>
          )}
        </div>

        {!isMobile && (
          <button
            type="button"
            name="Clear All Filters"
            title="Clear All Filters"
            aria-label="Clear All Filters"
            className="btn btn--small btn--link"
            onClick={clearFilters}
          >
            Clear All
          </button>
        )}
        {meta && meta.total_count > 0 && !isMobile && (
          <PaginationResults
            totalCount={parseInt(meta.total_count)}
            firstIndex={
              parseInt(meta.per_page) * parseInt(meta.current_page) -
              parseInt(meta.per_page) +
              1
            }
            lastIndex={Math.min(
              parseInt(meta.per_page) * parseInt(meta.current_page),
              parseInt(meta.total_count)
            )}
            queryType="listings"
          />
        )}
      </section>

      <section className="realty-one-logo-wrapper">
        <img src={realtyOneLogo} alt="Realty One Group" />
      </section>
    </div>
  );
}
